import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Crown, Monitor, History, Users, ShoppingBag, Clock, Play } from "lucide-react";
import MovieCard from "@/components/MovieCard";

export default function Account() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [history, setHistory] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [newProfile, setNewProfile] = useState("");
  const [loading, setLoading] = useState(true);

  const loadProfiles = () => api.get("/profiles").then((r) => setProfiles(r.data));

  useEffect(() => {
    Promise.all([
      loadProfiles(),
      api.get("/history").then((r) => setHistory(r.data)),
      api.get("/purchases").then((r) => setPurchases(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const addProfile = async () => {
    if (!newProfile.trim()) return;
    await api.post("/profiles", { name: newProfile.trim() });
    setNewProfile("");
    await loadProfiles();
    toast.success("Profile added");
  };

  const deleteProfile = async (pid) => {
    await api.delete(`/profiles/${pid}`);
    await loadProfiles();
    toast.success("Profile removed");
  };

  const devices = [
    { name: "Chrome — Windows", last: "Active now" },
    { name: "iPhone 15 Pro", last: "2 hours ago" },
    { name: "Samsung Smart TV", last: "Yesterday" },
  ];

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-ink"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>;

  return (
    <div className="bg-ink min-h-screen pt-24 px-4 sm:px-12 pb-20 space-y-12">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-black mb-1">Account</h1>
        <p className="text-white/60">{user?.name} · {user?.email}</p>
      </div>

      {/* Purchases & Rentals */}
      <section>
        <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2"><ShoppingBag className="w-6 h-6 text-gold" /> My Purchases &amp; Rentals</h2>
        {purchases.length === 0 ? (
          <p className="text-white/50">No purchases yet. Rent or buy a title to start watching.</p>
        ) : (
          <div className="bg-surface border border-white/10 rounded-2xl divide-y divide-white/5">
            {purchases.map((p) => (
              <div key={p.session_id} data-testid={`purchase-${p.session_id}`} className="flex items-center justify-between px-5 py-4 gap-4">
                <div className="min-w-0">
                  <p className="font-medium truncate">{p.content_title}{p.episode_id ? " · Episode" : ""}</p>
                  <p className="text-white/40 text-sm flex items-center gap-2">
                    <span className={`uppercase text-xs font-semibold ${p.mode === "buy" ? "text-gold" : "text-brand"}`}>{p.mode === "buy" ? "Purchased" : "Rented"}</span>
                    · ${p.amount} · {new Date(p.created_at).toLocaleDateString()}
                  </p>
                </div>
                <button onClick={() => navigate(`/watch/${p.content_id}${p.episode_id ? `?ep=${p.episode_id}` : ""}`)} className="bg-white text-black hover:bg-white/90 rounded-md font-semibold px-4 py-2 flex items-center gap-2 transition-colors shrink-0">
                  <Play className="w-4 h-4 fill-black" /> Watch
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Profiles */}
      <section>
        <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2"><Users className="w-6 h-6 text-gold" /> Profiles</h2>
        <div className="flex flex-wrap gap-4">
          {profiles.map((p) => (
            <div key={p.id} data-testid={`profile-${p.id}`} className="bg-surface border border-white/10 rounded-xl p-4 w-40 text-center relative group">
              <div className="w-16 h-16 mx-auto rounded-lg bg-gradient-to-br from-brand to-gold flex items-center justify-center font-display font-bold text-2xl uppercase mb-2">{p.name.charAt(0)}</div>
              <p className="font-semibold truncate">{p.name}</p>
              {p.kids && <span className="text-xs text-gold">Kids</span>}
              {profiles.length > 1 && (
                <button onClick={() => deleteProfile(p.id)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-white/60 hover:text-brand">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          <div className="bg-surface/50 border border-dashed border-white/20 rounded-xl p-4 w-40 flex flex-col items-center justify-center gap-2">
            <input data-testid="new-profile-input" value={newProfile} onChange={(e) => setNewProfile(e.target.value)} placeholder="Name" className="w-full bg-ink border border-white/10 rounded px-2 py-1.5 text-sm text-center outline-none focus:ring-1 focus:ring-brand" />
            <button data-testid="add-profile-btn" onClick={addProfile} className="flex items-center gap-1 text-sm text-gold hover:text-gold/80"><Plus className="w-4 h-4" /> Add Profile</button>
          </div>
        </div>
      </section>

      {/* Devices */}
      <section>
        <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2"><Monitor className="w-6 h-6 text-gold" /> Manage Devices</h2>
        <div className="bg-surface border border-white/10 rounded-2xl divide-y divide-white/5">
          {devices.map((d) => (
            <div key={d.name} className="flex items-center justify-between px-5 py-4">
              <div>
                <p className="font-medium">{d.name}</p>
                <p className="text-white/40 text-sm">{d.last}</p>
              </div>
              <button onClick={() => toast.success(`Signed out ${d.name}`)} className="text-sm text-white/60 hover:text-brand transition-colors">Sign out</button>
            </div>
          ))}
        </div>
      </section>

      {/* History */}
      <section>
        <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2"><History className="w-6 h-6 text-gold" /> Watch History</h2>
        {history.length === 0 ? (
          <p className="text-white/50">Nothing watched yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {history.map((item) => <MovieCard key={item.id} item={item} fluid />)}
          </div>
        )}
      </section>
    </div>
  );
}
